import moment from "moment";
import { getUserSetting, saveUserSetting } from "./UserSettingsService";

/**
 * The welcome modal's auto-show rules, as a pure function so they are testable without mounting
 * anything. The layout gathers the inputs and calls it; nothing here reads global state.
 */
export interface WelcomeGateInput {
    /** False = the configuration fetch has not landed. Never treated as "enabled". */
    tenantConfigLoaded: boolean;
    /** tenantConfig.disableWelcomeModal */
    welcomeDisabled: boolean;
    /** tenantConfig.sandboxMode. On a sandbox tenant `dismissed` is ignored. */
    sandboxMode: boolean;
    permissionCount: number;
    /** False = the bulk UserSettings payload has not landed, so `dismissed` is not yet knowable. */
    userSettingsLoaded: boolean;
    /** getUserSetting('welcome.dismissed') === 'true'; ignored when `sandboxMode`. */
    dismissed: boolean;
    /** $user.firstLoginDate: UTC with no zone marker. Null = the first login is happening now. */
    firstLoginDate: string | null;
    /** This user's 'welcome.shownOn.<userName>': the UTC date (YYYY-MM-DD) of their last auto-show. */
    shownOn: string | null;
    /**
     * The route renders the footer, which is what hosts the modal. The gate fires once per page
     * load, so firing where nothing can open the modal would spend it for nothing.
     */
    footerShown: boolean;
    now: Date;
}

/** Standard and sandbox tenants alike. */
export const WELCOME_WINDOW_DAYS = 7;

export const WELCOME_DISMISSED_KEY = "welcome.dismissed";
export const WELCOME_SHOWN_ON_KEY = "welcome.shownOn";

/** The UTC calendar date of an instant, as the once-per-day guard stores it. */
export function utcDateKey(date: Date): string {
    return moment.utc(date).format("YYYY-MM-DD");
}

export function shouldAutoShowWelcome(input: WelcomeGateInput): boolean {
    // Wait for both payloads. Firing early would either flash the modal at somebody who has
    // dismissed it, or show it on a tenant that has switched it off.
    if (!input.tenantConfigLoaded) return false;
    if (input.welcomeDisabled) return false;
    if (!input.userSettingsLoaded) return false;
    if (!input.footerShown) return false;

    // Mirrors the existing no-permissions banner state: somebody with no permissions has nothing
    // to be shown around.
    if (input.permissionCount < 1) return false;

    // A sandbox login is shared, so one trial user ticking the box must not silence the modal for
    // everyone else. The checkbox is not rendered there either.
    if (input.dismissed && !input.sandboxMode) return false;

    // At most one auto-show per user per browser per UTC day. A footer-link open never writes this.
    if (input.shownOn !== null && input.shownOn === utcDateKey(input.now)) return false;

    return isWithinWelcomeWindow(input.firstLoginDate, input.now);
}

/**
 * Whether the user is still in their first {@link WELCOME_WINDOW_DAYS} days.
 *
 * A null date means there is no login row yet, which counts as "the first login is happening now":
 * the very first sign-in returns null because the history row is written after the response. It
 * also covers a user who has not signed in since login history began recording, who is then
 * treated as new -- accepted, since nothing distinguishes them from a genuinely new user.
 */
export function isWithinWelcomeWindow(firstLoginDate: string | null, now: Date): boolean {
    if (!firstLoginDate) return true;

    // The API serializes UTC without a zone marker, so parse it as UTC explicitly rather than
    // letting moment read it in the browser's zone. ISO_8601 rather than a bare parse: moment
    // otherwise falls back to `new Date()` for anything it does not recognise, which is both
    // browser-dependent and noisy.
    const firstLogin = moment.utc(firstLoginDate, moment.ISO_8601);
    if (!firstLogin.isValid()) return true;

    const expiry = firstLogin.clone().add(WELCOME_WINDOW_DAYS, "days");
    return moment.utc(now).isBefore(expiry);
}

export function isWelcomeDismissed(): boolean {
    return getUserSetting(WELCOME_DISMISSED_KEY) === "true";
}

/** Only the modal's "Don't show this again" checkbox calls this. Starting a tour never does. */
export function dismissWelcome(): void {
    saveUserSetting(WELCOME_DISMISSED_KEY, "true");
}

/**
 * The once-per-day guard's storage key, one per user. localStorage belongs to the browser, and a
 * pharmacy workstation is often shared: an unscoped key let the first new user of the day silence
 * the auto-show for every other new user who signed in on that machine.
 */
export function welcomeShownOnKey(userName: string): string {
    return `${WELCOME_SHOWN_ON_KEY}.${userName.trim().toLowerCase()}`;
}

/**
 * Null without a user name, as for blocked storage. The gate cannot fire then anyway: the user and
 * their permissions arrive together, and a user with no permissions is never shown the modal.
 */
export function getWelcomeShownOn(userName: string | null | undefined): string | null {
    if (!userName) return null;
    try { return localStorage.getItem(welcomeShownOnKey(userName)); }
    catch { return null; }
}

/**
 * Records that the modal auto-opened today for this user, so their reloads and new tabs stay quiet
 * until tomorrow. localStorage rather than sessionStorage, which is per tab and so re-opened on
 * every new tab.
 */
export function markWelcomeShown(userName: string | null | undefined, now: Date = new Date()): void {
    if (!userName) return;
    try { localStorage.setItem(welcomeShownOnKey(userName), utcDateKey(now)); }
    catch { /* quota exceeded or storage blocked; the modal simply may reopen */ }
}
